import React, { useEffect, useState } from 'react';
import { FileMetadata } from '../../api/types/cms';

import FileVersionsDialog from '../dialog/FileVersionsDialog';
import ImagePreviewDialog from '../dialog/ImagePreviewDialog';
import FileDeleteDialog from '../dialog/FileDeleteDialog';
import FileUploadModal from '../qlive/FileUploadModal';
import useApi from '../../api';
import { useResponseHandler } from '../../utils/response';
import { useLocation } from 'react-router-dom';

export const EVENTS = {
    list_reload: 'cms.file.list_reload',
    open_file_upload: 'cms.open_upload',
    open_version_dialog: 'cms.file.version_dialog',
    open_delete_dialog: 'cms.file.delete_file',
    file_upload_success: 'cms.file_upload_success',
};

export function useFileRoutes() {
    const { openFileVersionDialogById } = useFileDialog();

    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const fileId = params.get('id');

    useEffect(() => {
        if (fileId) {
            openFileVersionDialogById(fileId);
        }
    }, [fileId]);
}

export function useFileDialog() {
    const { cms } = useApi();
    const { handleResponse } = useResponseHandler();

    const reloadFileList = () => {
        const event = new CustomEvent(EVENTS.list_reload);
        window.dispatchEvent(event);
    };

    const openFileVersionDialog = (file: FileMetadata) => {
        const event = new CustomEvent<FileMetadata>(
            EVENTS.open_version_dialog,
            {
                detail: file,
            },
        );

        window.dispatchEvent(event);
    };

    const openFileVersionDialogById = (fileId: string) => {
        cms.getFile(fileId).then(response => {
            handleResponse(response, () => {
                if (response) {
                    const event = new CustomEvent<FileMetadata>(
                        EVENTS.open_version_dialog,
                        {
                            detail: response,
                        },
                    );
                    window.dispatchEvent(event);
                }
            });
        });
    };

    const openFileDeleteDialog = (file: FileMetadata) => {
        const event = new CustomEvent<FileMetadata>(EVENTS.open_delete_dialog, {
            detail: file,
        });

        window.dispatchEvent(event);
    };

    return {
        reloadFileList,
        openFileVersionDialogById,
        openFileVersionDialog,
        openFileDeleteDialog,
    };
}

export default function CmsFileDialogs() {
    const { reloadFileList } = useFileDialog();

    const [fileUploadOpen, setFileUploadOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<FileMetadata>();
    const [selectedPreview, setSelectedPreview] = useState<FileMetadata>();
    const [selectedDelete, setSelectedDelete] = useState<FileMetadata>();

    const onFileUpload = () => {
        setFileUploadOpen(true);
    };

    const onFileUploadClose = () => {
        setFileUploadOpen(false);
    };

    const onFileUploadSuccess = () => {
        window.dispatchEvent(new Event(EVENTS.file_upload_success));
        onFileUploadClose();
    };

    /**
     * Adding global events
     */
    useEffect(() => {
        const onFileVersionDialog = (event: CustomEvent<FileMetadata>) => {
            setSelectedFile(event.detail);
        };

        const onFileDelete = (event: CustomEvent<FileMetadata>) => {
            setSelectedDelete(event.detail);
        };

        window.addEventListener(EVENTS.open_file_upload, onFileUpload);
        window.addEventListener(
            EVENTS.open_version_dialog,
            onFileVersionDialog as EventListener,
        );
        window.addEventListener(
            EVENTS.open_delete_dialog,
            onFileDelete as EventListener,
        );

        return () => {
            window.removeEventListener(EVENTS.open_file_upload, onFileUpload);
            window.removeEventListener(
                EVENTS.open_version_dialog,
                onFileVersionDialog as EventListener,
            );
            window.removeEventListener(
                EVENTS.open_delete_dialog,
                onFileDelete as EventListener,
            );
        };
    }, []);

    return (
        <>
            <FileUploadModal
                open={fileUploadOpen}
                onClose={onFileUploadClose}
                onSuccess={onFileUploadSuccess}
            />
            <FileVersionsDialog
                selectedFile={selectedFile}
                onClose={reload => {
                    if (reload) {
                        reloadFileList();
                    }
                    setSelectedFile(undefined);
                }}
                onPreview={setSelectedPreview}
            />
            <ImagePreviewDialog
                file={selectedPreview}
                onClose={() => setSelectedPreview(undefined)}
                editMode={false}
            />
            <FileDeleteDialog
                file={selectedDelete}
                onClose={deleted => {
                    if (deleted) {
                        reloadFileList();
                    }
                    setSelectedDelete(undefined);
                }}
            />
        </>
    );
}
